import type { FieldServiceProductType, QuoteDetailFilters } from '@/lib/types'
import type { QuoteData } from './quotes'

export type SiSearchFilters = {
  branchNumber: number | null
  searchText: string | null
  includeImported: boolean
  includeArchived: boolean
  pageNumber: number
  pageSize: number
}

export const QUERY_KEYS = {
  account: ['account'],
  primaryContact: ['primarycontact'],
  accountRooms: ['accountrooms'],
  systems: ['ecosystems'],
  systemScopeTags: ['systemscopetags'],
  phases: ['phases'],
  signature: ['signature'],
  paymentMilestones: ['paymentmilestones'],
  canCreatePaylink: ['can-create-paylink'],
  salesOrderPaymentMilestonesRoot: ['sales-order-payment-milestones'],
  salesOrderPaymentMilestones: (salesOrderId?: string | null) => [
    'sales-order-payment-milestones',
    { salesOrderId: salesOrderId || null },
  ],
  manufacturers: ['manufacturers'],
  quoteDetailsRoot: ['quotedetails'],
  storedSuppliesDetails: (quoteId?: string | null) => [
    'stored-supplies-details',
    { quoteId: quoteId || null },
  ],
  quoteDetailSummaryRoot: ['quotedetailsummary'],
  dtoolsSiSearchRoot: ['dtools-si', 'search'],
  dtoolsSiExportRoot: ['dtools-si', 'export'],
  dtoolsSiQuoteActions: (quoteId: string | null) => ['dtools-si', 'quote-actions', quoteId] as const,
  dtoolsSiExportDryRun: (quoteId: string | null) => ['dtools-si', 'export', 'dry-run', quoteId] as const,
  quote: (filters?: { previousQuote?: boolean }) => [
    'quote',
    {
      previousQuote: !!filters?.previousQuote,
    },
  ],
  originalQuote: (salesOrderId?: string | null) => ['original-quote', { salesOrderId: salesOrderId || null }],
  originalAgreementQuote: (salesOrderId?: string | null) => [
    'original-agreement-quote',
    { salesOrderId: salesOrderId || null },
  ],
  products: (filters?: {
    tags?: string[]
    manufacturer?: string | null
    search?: string
    types?: FieldServiceProductType[]
    productId?: string
  }) => [
    'products',
    {
      tags: filters?.tags?.length ? filters.tags.map((t) => t.toLowerCase()) : null,
      manufacturer: filters?.manufacturer?.trim().toLowerCase() || null,
      search: filters?.search?.trim().toLowerCase() || null,
      types: filters?.types || null,
      productId: filters?.productId || null,
    },
  ],
  quoteDetails: (filters?: QuoteDetailFilters) => [
    'quotedetails',
    {
      // filter group 1
      previousQuote: !!filters?.previousQuote,
      // filter group 2
      roomId: filters?.roomId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields._dsy_room_value || null,
      systemId: filters?.systemId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields.dsy_system || null,
      unassignedSystem: !!filters?.unassignedSystem,
      // filter group 3
      onlySummaryDetails: !!filters?.onlySummaryDetails,
    },
  ],
  quoteDetailSummary: (
    filters: QuoteDetailFilters | undefined | null,
    quote: QuoteData | undefined,
    quoteDetailsUpdatedAt: number,
    summaryDetailsUpdatedAt: number,
    // Room ids the summary actually reduced over — only set for room-group
    // (`includeChildRooms`) summaries, where re-parenting a child changes the
    // total without changing any other key member.
    roomGroupIds?: string[] | null
  ) => [
    'quotedetailsummary',
    {
      previousQuote: !!filters?.previousQuote,
      roomId: filters?.roomId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields._dsy_room_value || null,
      systemId: filters?.systemId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields.dsy_system || null,
      unassignedSystem: !!filters?.unassignedSystem,
      // Omitted entirely unless the group read is active: react-query hashes the
      // object's own keys, so an always-present `includeChildRooms: false` would
      // rewrite every existing summary key (orphaning caches and breaking the
      // invalidation targeting that matches on these members).
      // Sorted: the group's membership is what the total depends on, not the
      // (seq, id) order it arrives in — otherwise a sibling drag-reorder orphans
      // and recomputes every group summary for an unchanged set of rooms.
      ...(filters?.includeChildRooms
        ? { includeChildRooms: true, roomGroupIds: roomGroupIds ? [...roomGroupIds].sort() : null }
        : {}),
    },
    {
      // Add filters that are used in the query fn
      laborKey: filters?.laborKey || null,
      search: filters?.search?.trim()?.toLowerCase() || null,
      summaryDetails: filters?.onlySummaryDetails || 'include',
      quoteDetailId: filters?.quoteDetail?.quoteDetail?.quotedetailid || null,
      includeAccessories: !!filters?.quoteDetail?.includeAccessories,
      // A package header has two summaries — its own ($0) and the derived
      // package total — over the same row id, so the key has to tell them apart.
      includeMembers: !!filters?.quoteDetail?.includeMembers,
      unitOnly: !!filters?.quoteDetail?.unitOnly,
      // Add data that is used in the query fn
      taxRate: quote?.dsy_taxcode?.msdyn_taxrate || null,
      suppliesPriceRate: quote?.dsy_suppliespricerate || null,
      suppliesCostRate: quote?.dsy_suppliescostrate || null,
      discountAmount: quote?.discountamount || null,
      discountPercentage: quote?.discountpercentage || null,
      statecode: quote?.statecode ?? null,
      dtoolsSiProjectId: quote?.dsy_dtoolssiprojectid || null,
      estimatedCost: quote?.msdyn_estimatedcost ?? null,
      totalLineItemAmount: quote?.totallineitemamount ?? null,
      totalDiscountAmount: quote?.totaldiscountamount ?? null,
      totalTax: quote?.totaltax ?? null,
      totalAmount: quote?.totalamount ?? null,
      totalHours: quote?.dsy_totalhours ?? null,
    },
    {
      quoteDetailsUpdatedAt: quoteDetailsUpdatedAt,
      summaryDetailsUpdatedAt: summaryDetailsUpdatedAt,
    },
  ],
  quoteDetailsChanges: (
    filters: QuoteDetailFilters | undefined | null,
    quoteDetailsUpdatedAt: number,
    previousQuoteDetailsUpdatedAt: number,
    // Room ids the diff actually covered — only set for room-group
    // (`includeChildRooms`) reads. Same omission rule as `quoteDetailSummary`.
    roomGroupIds?: string[] | null
  ) => [
    'quotedetailschanges',
    {
      roomId: filters?.roomId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields._dsy_room_value || null,
      systemId: filters?.systemId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields.dsy_system || null,
      unassignedSystem: !!filters?.unassignedSystem,
      // Sorted for the same reason as `quoteDetailSummary` above.
      ...(filters?.includeChildRooms
        ? { includeChildRooms: true, roomGroupIds: roomGroupIds ? [...roomGroupIds].sort() : null }
        : {}),
    },
    {
      quoteDetailsUpdatedAt: quoteDetailsUpdatedAt,
      previousQuoteDetailsUpdatedAt: previousQuoteDetailsUpdatedAt,
    },
  ],
  quoteDetailsChangesSummary: (
    filters: QuoteDetailFilters | undefined | null,
    currentSummaryUpdatedAt: number,
    previousSummaryUpdatedAt: number,
    // Room ids both sides' summaries reduced over — only set for room-group
    // (`includeChildRooms`) reads. Same omission rule as `quoteDetailSummary`.
    roomGroupIds?: string[] | null
  ) => [
    'changessummary',
    {
      roomId: filters?.roomId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields._dsy_room_value || null,
      systemId: filters?.systemId || filters?.quoteDetail?.quoteDetail.dsy_cpqfields.dsy_system || null,
      unassignedSystem: !!filters?.unassignedSystem,
      // Without this a group delta and the same room's exact delta hash
      // IDENTICALLY — the only other difference is the two `*UpdatedAt` numbers,
      // and both sides are synchronous reducer queries that routinely settle in
      // the same millisecond. Whichever mounted first then wins for both, so the
      // changes tab shows either exact-room totals in the summary card or a
      // whole-group delta on a parent section header that already has its child
      // sections rendered beneath it. Sorted + omitted-when-false exactly as in
      // `quoteDetailSummary`.
      ...(filters?.includeChildRooms
        ? { includeChildRooms: true, roomGroupIds: roomGroupIds ? [...roomGroupIds].sort() : null }
        : {}),
    },
    {
      // Add filters that are used in the query fn
      laborKey: filters?.laborKey || null,
      // Both sides' summaries are search-scoped, so the delta is too — without
      // this, two searches whose summaries settle on the same millisecond share
      // one cache entry (same class of collision as the room group above).
      search: filters?.search?.trim()?.toLowerCase() || null,
      summaryDetails: filters?.onlySummaryDetails || 'include',
      quoteDetailId: filters?.quoteDetail?.quoteDetail?.quotedetailid || null,
      includeAccessories: !!filters?.quoteDetail?.includeAccessories,
      unitOnly: !!filters?.quoteDetail?.unitOnly,
    },
    {
      currentSummaryUpdatedAt: currentSummaryUpdatedAt,
      previousSummaryUpdatedAt: previousSummaryUpdatedAt,
    },
  ],
  accessoryPriceListItems: ({
    productId,
    priceListId,
  }: {
    productId: string | undefined | null
    priceListId: string | undefined | null
  }) => [
    'dsy_pricelistitemaccessories',
    {
      productId: productId || null,
      priceListId: priceListId || null,
    },
  ],
  // Root, not the filtered key: rewriting a package's contents has to clear
  // every cached pricing of it, whatever price list produced it.
  packageContentPriceListItemsRoot: ['dsy_productaccessories'],
  packageContentPriceListItems: ({
    productId,
    priceListId,
  }: {
    productId: string | undefined | null
    priceListId: string | undefined | null
  }) => [
    'dsy_productaccessories',
    {
      productId: productId || null,
      priceListId: priceListId || null,
    },
  ],
  // Roots, not the filtered keys: saving or hiding a package has to clear every
  // cached search, whatever tags or text produced it.
  catalogPackagesRoot: ['catalog-packages'],
  productsRoot: ['products'],
  catalogPackages: (priceListId?: string | null) => ['catalog-packages', { priceListId: priceListId || null }],
  // Under the catalog-packages root so saving or renaming a package clears the
  // name checks along with the lists.
  catalogPackageNameCheck: (name: string) => ['catalog-packages', 'name-check', { name }],
  taxCodes: (search?: string | null) => ['taxcodes', { search: search || null }],
  notTaxableTaxCode: ['taxcodes', 'not-taxable'],
  priceLevels: ['pricelevels'],
  branches: ['branches'],
  branchClientLogo: (branchId?: string | null) => ['branch-client-logo', { branchId: branchId || null }],
  salesRepSearch: (search: string) => ['salesreps', { search }] as const,
  accountContacts: (accountId: string | null) => ['account-contacts', { accountId }] as const,
  accountBillToContact: (accountId: string | null) => ['account-bill-to-contact', { accountId }] as const,
  contactSearch: (search: string) => ['contact-search', { search }] as const,
  netSuiteChangeOrderURL: (netsuiteChangeOrderId?: number | null) => [
    'netSuiteChangeOrderURL',
    { netsuiteChangeOrderId: netsuiteChangeOrderId || null },
  ],
  outdatedProducts: ['outdatedProducts'],
  proposal: ['proposal'],
  currentUserEmailSignature: ['currentUserEmailSignature'],
  productSearch: (search: string, options?: { laborOnly?: boolean; standardLaborOnly?: boolean; otherChargeOnly?: boolean }) => [
    'productSearch',
    {
      search: search.trim().toLowerCase(),
      laborOnly: options?.laborOnly === true,
      standardLaborOnly: options?.standardLaborOnly === true,
      otherChargeOnly: options?.otherChargeOnly === true,
    },
  ],
  exactProduct: (productNumber: string) => [
    'exactProduct',
    { productNumber: productNumber.trim().toUpperCase() },
  ],
  referenceTV: ({ priceListId, productIds }: { priceListId: string; productIds: string[] }) => [
    'referenceTV',
    { priceListId, productIds: [...productIds].sort() },
  ],
  inventory: ({ branchId, productIds }: { branchId: string | null; productIds: string[] }) =>
    ['inventory', { branchId, productIds: [...productIds].sort() }] as const,
  productImage: (productId: string) => ['productImage', productId],
  templates: (filters?: { priceListId?: string | null }) => [
    'templates',
    {
      priceListId: filters?.priceListId || null,
    },
  ],
  dtoolsSiSearch: (filters: SiSearchFilters) => ['dtools-si', 'search', filters] as const,
  dtoolsSiProjectDetail: (
    branch: number | null,
    id: string | null,
    number: string | null,
    coNumber?: number | null,
    replaceExisting = false
  ) => ['dtools-si', 'detail', branch, id, number, coNumber ?? null, replaceExisting] as const,
} as const
